import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import { RxType } from '~/api/graphql/generated/types'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'
import RxRequestCatalogItemNotLinked from '~/components/dashboard/prescription/prescription-editor/RxRequestCatalogItemNotLinked'
import VetDietInfo from '~/components/dashboard/prescription/prescription-editor/VetDietInfo'
import FeatureToggle from '~/constants/featureToggle'
import {
  getFeatureToggle,
  getPrescriptionProductSizeUnit,
} from '~/store/reducers/constants'
import type { Prescription } from '~/types'

import styles from './QuantityAndRefills.module.scss'

interface QuantityAndRefillsProps {
  isVetDiet: boolean
  prescription: Prescription
}

const QuantityAndRefills = ({
  prescription,
  isVetDiet,
}: QuantityAndRefillsProps) => {
  const { t } = useTranslation(['Plurals', 'Clients'])

  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const getCatalogItemKey = () => {
    if (isFoodCatalogEnabled) {
      return 'globalVariationMappingId'
    }
    if (prescription.rxType === RxType.VetDiet) {
      return 'globalFoodCatalogVariationId'
    }
    return 'globalVariationId'
  }

  const isChewyCatalogItemNotLinked = R.isNil(prescription[getCatalogItemKey()])

  const { quantityNumbRefills } = prescription

  const quantity =
    prescription.totalQuantity ?? getHighValue(prescription.quantity) ?? 0

  const unitOfProduct = prescriptionProductSizeUnit.find(
    (unit) => unit.id === prescription.totalQuantityUnitId,
  )

  const vetDietDescription = (
    <>
      {`${quantity} `}
      {isChewyCatalogItemNotLinked ? (
        <RxRequestCatalogItemNotLinked sx={{ mb: 2 }} />
      ) : (
        <VetDietInfo drugInfo={prescription.drugInfo} />
      )}
    </>
  )

  const quantityText = unitOfProduct?.name
    ? `${quantity} ${unitOfProduct.name}`
    : `${quantity}`

  const refillText =
    quantityNumbRefills && quantityNumbRefills > 0
      ? ` | ${t('Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS', {
          number: quantityNumbRefills,
        })}`
      : ` | ${t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`

  return (
    <div className={styles.container}>
      {!isVetDiet ? quantityText : vetDietDescription}
      {refillText}
    </div>
  )
}

export default QuantityAndRefills
